import { useState } from "react";

import { Outlet, redirect, useLoaderData, useRouteError } from "react-router";

import { AlertCircleIcon, LogOutIcon, RefreshCwIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

import { NotificationBell } from "@/features/notifications/components/NotificationBell";
import { BugTrackerLogo } from "@/pages/AuthLayout";
import { DesktopNav } from "@/shared/components/DesktopNav";
import GlobalSearchBar from "@/shared/components/GlobalSearchBar";
import { MobileNav } from "@/shared/components/MobileNav";
import { UserMenu } from "@/shared/components/UserMenu";
import { QUERY_EXPIRATIONS, QUERY_KEYS } from "@/shared/lib/constants/queries";
import { getMyPermissions } from "@/shared/lib/services/user";
import { AppError, getRouteErrorMessage } from "@/shared/lib/utils/errors";
import queryClient from "@/shared/lib/utils/queryClient";
import { deleteSession, getSession } from "@/shared/lib/utils/session";

export async function appLayoutLoader() {
  const session = getSession();

  if (!session) {
    throw redirect("/account/login");
  }

  const permissions = await queryClient.fetchQuery<string[], AppError | Error>({
    queryKey: [QUERY_KEYS.GET_MY_PERMISSIONS],
    queryFn: getMyPermissions,
    staleTime: QUERY_EXPIRATIONS.GET_MY_PERMISSIONS,
    retry: false,
  });

  return { permissions };
}

export function AppLayoutLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted">
      <div className="flex flex-col items-center gap-4 border border-border bg-background p-18 shadow-sm">
        <BugTrackerLogo />
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner className="size-4" />
          Loading...
        </div>
      </div>
    </div>
  );
}

export function AppLayoutError() {
  const error = useRouteError();
  const { title, description, status } = getRouteErrorMessage(error);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted">
      <div className="flex w-full max-w-sm flex-col items-center gap-6 border border-border bg-background p-8 shadow-sm text-center">
        <BugTrackerLogo />
        <div className="flex flex-col items-center gap-3">
          <div className="flex size-14 items-center justify-center bg-destructive/10">
            <AlertCircleIcon className="size-7 text-destructive" />
          </div>
          <div className="flex flex-col gap-1.5">
            <h1 className="font-heading text-base font-semibold">{title}</h1>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          {status !== undefined && <span className="font-mono text-xs font-semibold text-destructive">HTTP {status}</span>}
        </div>
        <div className="flex w-full flex-col gap-2">
          <Button size="sm" onClick={() => window.location.reload()}>
            <RefreshCwIcon />
            Try again
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              deleteSession();
              window.location.href = "/account/login";
            }}
          >
            <LogOutIcon />
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { permissions } = useLoaderData() as { permissions: string[] };

  return (
    <div className="flex min-h-screen flex-col bg-muted">
      <header className="sticky top-0 z-40 w-full border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-4 px-4 sm:px-6">
          <BugTrackerLogo />
          <DesktopNav permissions={permissions} />
          <GlobalSearchBar permissions={permissions} />
          <NotificationBell permissions={permissions} />
          <UserMenu />
          <MobileNav permissions={permissions} open={mobileMenuOpen} onOpenChange={setMobileMenuOpen} />
        </div>
      </header>
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6">
        <Outlet context={{ permissions }} />
      </main>
    </div>
  );
}
